import { Component, effect, OnDestroy, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { setOptionsForPrimeNg } from '../../../PrimeNG/utils';
import { EnumValues, DynamicFilterConfig, DynamicFilterValues, createDynamicFilterState } from '@card-software/library';
import { EnumService } from '../../../services/enum.service';
import { WorkOrderRoles } from '../../../models/roles/roles-group';
import { WorkOrder, WorkOrderSearchCommand } from '../../../models/order/work-order';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { BusinessSearchCommand } from '../../../models/business-search';
import { WorkOrderReportService } from '../../../services/work-order-report.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { AuthService } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-work-orders',
  templateUrl: './work-orders.component.html',
  styleUrl: './work-orders.component.scss',
})
export class WorkOrdersComponent implements OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translate = inject(TranslateService);
  private workOrderProxyService = inject(WorkOrderProxyService);
  private workOrderReportService = inject(WorkOrderReportService);
  private businessProxyService = inject(BusinessProxyService);

  isLoading = signal(0);
  listWorkOrders = signal<WorkOrder[]>([]);
  orderTypesEnums = signal<EnumValues>({});
  orderStatusesEnums = signal<EnumValues>({});
  orderMenuitems = signal<MenuItem[]>([]);
  enumsReady = signal(false);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'work-order-filters', config: this.filterConfig });
  showFilters = signal(false);
  orderType = signal<number>(0);
  filteredCustomers = signal<Business[]>([]);
  filteredBrands = signal<Business[]>([]);
  currentPage = signal<number>(1);
  totalRecords = signal<number>(0);
  pageSize = signal<number>(25);
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  canEdit = computed(() => {
    if (AuthService.hasRole(WorkOrderRoles.WorkOrderAdmin) || AuthService.hasRole(WorkOrderRoles.WorkOrderEditor)) {
      return true;
    }
    if (AuthService.hasRole(WorkOrderRoles.WorkOrderViewer)) {
      return false;
    }
    return false;
  });

  private destroy$ = new Subject<void>();
  private filtersPanelInitialized = false;
  private lastSearchCriteria: WorkOrderSearchCommand = {};

  getRowData(rowData: unknown): WorkOrder {
    return rowData as WorkOrder;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    effect(
      () => {
        const orderTypesValue = this.enumService.getEnum('WorkOrderType', 'orderEnums').values;
        const orderStatusesValue = this.enumService.getEnum('WorkOrderStatus', 'orderEnums').values;

        if (Object.keys(orderTypesValue).length > 0 && Object.keys(orderStatusesValue).length > 0) {
          this.orderTypesEnums.set(orderTypesValue);
          this.orderStatusesEnums.set(orderStatusesValue);
          this.setOrderTypeMenu();
          this.enumsReady.set(true);
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translate.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translate.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translate.instant(key);
      return translation === key ? fallback : translation;
    };

    const orderTypeOptions = setOptionsForPrimeNg(this.orderTypesEnums()).map((option) => ({
      value: option.value,
      label: option.label,
    }));

    const orderStatusOptions = setOptionsForPrimeNg(this.orderStatusesEnums()).map((option) => ({
      value: option.value,
      label: option.label,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'orderNumber',
          label: translate('Work_Orders_Filters_Order_Number', 'Order Number'),
          placeholder: translate('Enter_order_number', 'Enter order number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'customerProductOrder',
          label: translate('Work_Orders_Filters_Customer_Product_Order', 'Client Product Order'),
          placeholder: translate('Enter_customer_product_order', 'Enter client product order'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'orderType',
          label: translate('Work_Orders_Filters_Order_Type', 'Order Type'),
          placeholder: translate('Select_order_type', 'Select order type'),
          options: orderTypeOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Work_Orders_Filters_Statuses', 'Statuses'),
          placeholder: translate('SelectStatuses', 'Select statuses'),
          options: orderStatusOptions,
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'customerReference',
          label: translate('Work_Orders_Filters_Client', 'Client'),
          placeholder: translate('Enter_client', 'Enter client'),
          autocompleteConfig: {
            suggestions: this.filteredCustomers(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterCustomers(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'autocomplete',
          formControlName: 'brandReference',
          label: translate('Work_Orders_Filters_Brand', 'Brand'),
          placeholder: translate('Enter_brand', 'Enter brand'),
          autocompleteConfig: {
            suggestions: this.filteredBrands(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterBrands(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveBusiness(id: string): Observable<Business | null> {
    return this.businessProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private onSearch(searchCriteria: WorkOrderSearchCommand): void {
    this.startLoading();
    this.lastSearchCriteria = searchCriteria;
    this.workOrderProxyService
      .searchWorkOrder(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const pagedResult = response.data;
          this.listWorkOrders.set(pagedResult?.items ?? []);
          this.totalRecords.set(pagedResult?.totalCount ?? 0);
        },
        error: (e) => {
          console.error(e);
          this.listWorkOrders.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    const newField = this.normalizeSortField(event.field, this.sortField());
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const searchCriteria: WorkOrderSearchCommand = {
      orderNumber: (filters['orderNumber'] as string)?.trim() || undefined,
      customerProductOrder: (filters['customerProductOrder'] as string)?.trim() || undefined,
      orderType: (filters['orderType'] as number) ?? undefined,
      statuses:
        Array.isArray(filters['statuses']) && (filters['statuses'] as unknown[]).length > 0
          ? (filters['statuses'] as unknown[]).map((item: unknown) =>
              typeof item === 'object' && item !== null ? (item as Record<string, number>)['value'] : (item as number),
            )
          : undefined,
      customerReference:
        typeof filters['customerReference'] === 'object'
          ? (filters['customerReference'] as Record<string, string>)?.['id']
          : (filters['customerReference'] as string)?.trim() || undefined,
      brandReference:
        typeof filters['brandReference'] === 'object'
          ? (filters['brandReference'] as Record<string, string>)?.['id']
          : (filters['brandReference'] as string)?.trim() || undefined,
    };

    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  filterCustomers(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim() || '';
    if (query.length < 2) {
      this.filteredCustomers.set([]);
      this.updateAutocompleteSuggestions('customerReference', []);
      return;
    }
    const command: BusinessSearchCommand = {
      name: query,
      code: query,
      businessTypes: [BusinessEntityType.Client],
    };
    this.businessProxyService
      .search(command, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const items = response.data?.items ?? [];
          this.filteredCustomers.set(items);
          this.updateAutocompleteSuggestions('customerReference', items);
        },
        error: () => {
          this.filteredCustomers.set([]);
          this.updateAutocompleteSuggestions('customerReference', []);
        },
      });
  }

  filterBrands(event: AutoCompleteCompleteEvent): void {
    const query = event.query?.trim() || '';
    if (query.length < 2) {
      this.filteredBrands.set([]);
      this.updateAutocompleteSuggestions('brandReference', []);
      return;
    }
    const command: BusinessSearchCommand = {
      name: query,
      code: query,
      businessTypes: [BusinessEntityType.Brand],
    };
    this.businessProxyService
      .search(command, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const items = response.data?.items ?? [];
          this.filteredBrands.set(items);
          this.updateAutocompleteSuggestions('brandReference', items);
        },
        error: () => {
          this.filteredBrands.set([]);
          this.updateAutocompleteSuggestions('brandReference', []);
        },
      });
  }

  private updateAutocompleteSuggestions(formControlName: string, suggestions: unknown[]): void {
    const currentConfig = this.filterConfig();
    if (!currentConfig) return;
    const updatedFilters = currentConfig.filters.map((filter) => {
      if (filter.formControlName === formControlName && filter.autocompleteConfig) {
        return { ...filter, autocompleteConfig: { ...filter.autocompleteConfig, suggestions } };
      }
      return filter;
    });
    this.filterConfig.set({ ...currentConfig, filters: updatedFilters });
  }

  setOrderTypeMenu(): void {
    const menuItems = setOptionsForPrimeNg(this.orderTypesEnums())
      .filter((orderType) => orderType.label !== 'Short Fall' && orderType.label !== 'ShortFall')
      .map((orderType) => {
        return {
          label: orderType.label,
          command: () => {
            this.orderType.set(orderType.value);
            this.redirectToDetails('new', true);
          },
        };
      });

    this.orderMenuitems.set(menuItems);
  }

  public redirectToDetails(orderId: string, orderTypeActive = false): void {
    const queryParams = orderTypeActive ? { id: orderId, orderType: this.orderType() } : { id: orderId };
    this.router.navigate(['/work-order-details'], { queryParams });
  }

  getCustomerProductOrder(workOrder: WorkOrder): string {
    return workOrder.customerProductOrders.map((order) => order.productOrderNumber).join(', ');
  }

  printReportPdf(workOrderId: string, event: Event): void {
    event.stopPropagation();
    this.workOrderReportService.generatePdfReport(workOrderId);
  }

  copyWorkOrder(workOrderId: string, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/work-order-details'], {
      queryParams: {
        id: workOrderId,
        isCopy: true,
        isShortFall: false,
      },
    });
  }

  createShortFall(workOrderId: string, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/work-order-details'], {
      queryParams: {
        id: workOrderId,
        isCopy: true,
        isShortFall: true,
      },
    });
  }

  private normalizeSortField(sortField: string | string[] | undefined | null, fallback: string | undefined): string | undefined {
    if (Array.isArray(sortField)) {
      return sortField[0] ?? fallback;
    }
    return sortField ?? fallback;
  }
}
